import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RcsApi } from 'entities/Rcs'
import { RcsPath } from 'pages/settings/pages/rcs/route/rcsPath'
import { RcsCreateProfileStore } from './RcsCreateProfileStore'
import type { IRcsPreviewTabs } from './type'

export class RcsCreateStore {
  private _loading = false
  private _profileStore: RcsCreateProfileStore
  private _activeTab: IRcsPreviewTabs = 'profile'

  constructor(private _entities: IEntitiesStore) {
    this._profileStore = new RcsCreateProfileStore(this._entities)

    makeAutoObservable(this)
    this.init()
  }

  get activeTab() {
    return this._activeTab
  }

  setActiveTab = (tab: IRcsPreviewTabs) => {
    this._activeTab = tab
  }

  get previewTabs() {
    return [
      {
        label: 'Business info',
        value: 'profile',
      },
      {
        label: 'Conversation',
        value: 'conversation',
      },
    ]
  }

  get previewData() {
    const profileData = {
      logoUrl: this.profileStore.logoUrl,
      bannerUrl: this.profileStore.bannerUrl,
      brandName: this.profileStore.brandName,
      brandDescription: this.profileStore.brandDescription,
      numbers: this.profileStore.numbers,
      websites: this.profileStore.websites,
      hasNumbers: this.profileStore.hasNumbers,
      hasWebsites: this.profileStore.hasWebsites,
      hasEmails: this.profileStore.hasEmails,
      emails: this.profileStore.emails,
      privacyPolicy: this.profileStore.privacyPolicyUrl,
      terms: this.profileStore.termsAndConditionsUrl,
      accentColor: this.profileStore.accentColor,
    }

    return profileData
  }

  get loading() {
    return this._loading
  }

  get profileStore() {
    return this._profileStore
  }

  get profileParams() {
    return this._profileStore.profileParams
  }

  create = async () => {
    if (this._profileStore.validate()) return

    try {
      runInAction(() => {
        this._loading = true
      })

      await RcsApi.saveRcs(this.profileParams)
      uiStore.changeRoute({
        path: RcsPath.root,
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        this._profileStore.initErrors(e.errors)
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await RcsApi.getRcs()

      if (data?.length) {
        uiStore.changeRoute({
          method: 'replace',
          path: RcsPath.root,
        })
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  reset = () => {
    this._loading = false
    this._profileStore.reset()
  }

  dispose = () => {
    this._profileStore.dispose()
  }
}
